import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import Layout from '../components/Layout';
import FormPageTitle from '../components/FormPageTitle';
import Pagination from '../components/Pagination';
import { categoryService, CategoryTreeItem } from '../services/categoryService';
import { authService, User } from '../services/authService';
import { useTranslation } from '../contexts/LocaleContext';
import { ROUTES, APP_CONFIG } from '../constants';

const Categories: React.FC = () => {
  const t = useTranslation();
  const [categories, setCategories] = useState<CategoryTreeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    loadCategories(currentPage, appliedSearch);
  }, [currentPage, appliedSearch]);

  const loadUser = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const loadCategories = async (page: number = 1, search?: string) => {
    setLoading(true);
    try {
      const response = await categoryService.getCategoriesList(
        page,
        APP_CONFIG.DEFAULT_PAGE_SIZE,
        search
      );
      setCategories(response.data);
      setCurrentPage(page);
      setLastPage(response.last_page);
      setTotalCount(response.total ?? 0);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete category?')) {
      try {
        await categoryService.deleteCategory(id);
        loadCategories(currentPage);
      } catch (error) {
        console.error('Error deleting category:', error);
        alert(t('errors.deleteCategory'));
      }
    }
  };

  const hasPermission = (permission: string) => {
    return user?.permissions?.includes(permission) || false;
  };

  const canCreate = hasPermission('categories.create');
  const canDelete = hasPermission('categories.delete');
  const canUpdate = hasPermission('categories.update');

  const handleToggleVisible = async (id: number, currentVisible: boolean) => {
    try {
      await categoryService.setCategoryVisibility(id, !currentVisible);
      loadCategories(currentPage);
    } catch (error) {
      console.error('Error toggling category visibility:', error);
      alert(t('errors.toggleCategoryVisibility'));
    }
  };

  return (
    <Layout>
      <FormPageTitle title={t('categories')} helperContent={t('helpers.category.title')} />

      {canCreate && (
        <Row className="my-2 mx-0 d-flex justify-content-between gap-2">
          <Col xs={12} className="p-0">
            <Form onSubmit={handleSearch} className="d-flex">
              <Form.Control
                type="text"
                placeholder={t('category.enter')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button type="submit" variant="success" className="ms-2">
                {t('manage.search')}
              </Button>
            </Form>
          </Col>
          <Col xs="auto" className="d-flex justify-content-center p-0">
            <Link to={ROUTES.CATEGORY_CREATE}>
              <Button
                variant="primary"
                className="text-white"
              >
                {t('categoryCreateBtn')}
              </Button>
            </Link>
          </Col>
        </Row>
      )}

      {loading && (
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      )}
      {!loading && categories.length === 0 && (
        <div className="text-secondary text-center my-2">{t('category.notFound')}</div>
      )}

      {!loading && categories.map((category) => {
        const childNames = category.childrens?.length ? category.childrens : category.children;
        return (
          <Row
            key={category.id}
            className={`p-2 text-bg-light my-2 mx-0 border rounded ${category.isVisible === false ? 'opacity-75' : ''}`}
          >
            <Col>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h5 className="m-0 d-flex  gap-2 flex-column">
                    {category.name}
                    <span className="d-flex gap-2 flex-wrap">
                      {canUpdate && (
                        <Button
                          size="sm"
                          variant={category.isVisible !== false ? 'outline-warning' : 'outline-success'}
                          onClick={() => handleToggleVisible(category.id, category.isVisible !== false)}
                        >
                          {category.isVisible !== false ? t('category.disable') : t('category.enable')}
                        </Button>
                      )}
                      {category.isVisible === false ? (
                        <span className="badge bg-secondary d-inline-flex align-items-center justify-content-center">{t('category.isDisabled')}</span>
                      ) : (
                        <span className="badge bg-success d-inline-flex align-items-center justify-content-center">{t('category.isEnabled')}</span>
                      )}
                    </span>

                  </h5>
                  {category.hasChild && childNames && childNames.length > 0 && (
                    <small className="text-muted">{childNames.join(', ')}</small>
                  )}
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Link
                    to={ROUTES.CATEGORY_PROMOCODE_TEMPLATES(category.id)}
                    className="btn btn-secondary"
                  >
                    {t('category.promocodeTemplates')}
                  </Link>
                  {canUpdate && (
                    <Link
                      to={ROUTES.CATEGORY_EDIT(category.id)}
                      className="btn btn-outline-primary"
                    >
                      {t('manage.edit')}
                    </Link>
                  )}
                  {canDelete && (
                    <Button
                      variant="danger"
                      onClick={() => handleDelete(category.id)}
                    >
                      {t('manage.delete')}
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        );
      })}
      {(!loading && lastPage > 1) && (
        
            <Pagination
              currentPage={currentPage}
              lastPage={lastPage}
              totalCount={totalCount}
              pageSize={APP_CONFIG.DEFAULT_PAGE_SIZE}
              onPageChange={(page) => setCurrentPage(page)}
            />
          
      )}
    </Layout>
  );
};

export default Categories;
